import { Link } from "react-router-dom";
import GridWrapper from "../../gridWrapper";
import Box from "../box";
import { IconBuilder } from "/app/src/components/icons/IconBuilder";
import { ReactNode } from "react";
import { Divider } from "@heroui/react";

type InfoItem = {
  label: string;
  // Allow JSX/React nodes so callers can pass components (like formatted dates)
  value?: ReactNode | null;
  link?: string;
};

/**
 * Renders a list of information items with an optional header.
 * @param {string} [header] Optional header text displayed above the list.
 * @param {InfoItem[]} items Array of items to display with label, value, and optional link.
 * @returns {JSX.Element} The rendered info list component.
 */
export default function InfoList({
  header,
  items,
  infoFields,
}: {
  header?: string;
  items: InfoItem[];
  infoFields?: InfoItem[];
}) {
  /**
   * Checks if the provided value is neither null, undefined, nor an empty string.
   * @param val - The value to check.
   * @returns True if the value is present and not an empty string, otherwise false.
   */
  const hasValue = (val: InfoItem["value"]) => {
    if (val === null || val === undefined) return false;
    // empty string should be considered no-value
    if (typeof val === "string") return val.trim() !== "";
    return true; // numbers, React nodes, elements, etc. count as present
  };

  return (
    <Box>
      {header && <h3 className="text-[16px] font-medium mb-4">{header}</h3>}
      <GridWrapper responsive={{ sm: 1, md: 3, lg: 4 }}>
        {items.map(({ label, value, link }) => {
          const valuePresent = hasValue(value);

          return (
            <div key={label} className="flex flex-col gap-1">
              <span className="text-black font-medium text-sm">{label}</span>

              {valuePresent && link ? (
                <Link
                  to={link}
                  className="text-primary-default flex items-center gap-1"
                >
                  {String(value)}
                  <IconBuilder icon="ExternalLink" size={16} color="#1475D7" />
                </Link>
              ) : valuePresent ? (
                <span>{value}</span>
              ) : (
                <span className="text-neutral-smoke text-sm">-</span>
              )}
            </div>
          );
        })}
      </GridWrapper>
      {infoFields && infoFields.length > 0 && <Divider className="my-6" />}
      <GridWrapper responsive={{ sm: 1, md: 3, lg: 4 }}>
        {infoFields?.map(({ label, value }) => {
          return (
            <div key={label} className="flex flex-col gap-1">
              <span className="text-black font-medium text-sm">{label}</span>
              {hasValue(value) ? (
                <span>{value}</span>
              ) : (
                <span className="text-neutral-smoke text-sm">-</span>
              )}
            </div>
          );
        })}
      </GridWrapper>
    </Box>
  );
}
